import {
  type HomeworkAnswer,
  type HomeworkItem,
  OFFLINE_ACTIONS,
  type Submission,
  type ViewContext,
  addDays,
  homeworkToDo,
  shortDate,
  todayIso,
  useHomeworkActions,
  useQueuedActions,
  useStudentHomework,
} from '@shkola/api';
import { type Swatch, subjectLook } from '@shkola/design';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { type CSSProperties, type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerFiles, AttachmentList } from './AnswerFiles';
import { SpeakButton } from '../components/SpeakButton';
import { FolderTile, NotebookSpread, Pin, Sticker } from '../design/kit';

type StudentContext = Extract<ViewContext, { kind: 'student' }>;

const TILTS = [-2.2, 1.6, -1.1, 2.1, -1.6];

/** A paper note pinned or clipped to the page (not redrawn in v4 yet). */
function StickyNote({ hw, index }: { hw: HomeworkItem; index: number }) {
  const { t } = useTranslation();
  const today = todayIso();
  const due =
    hw.dueOn < today
      ? t('student.todo.overdue')
      : hw.dueOn === today
        ? t('student.todo.today')
        : t('student.todo.tomorrow');
  const look = subjectLook(hw.subject);
  return (
    <Link
      to="/homework"
      search={{ course: hw.courseId }}
      className="motion-rise relative flex min-h-[150px] flex-col gap-1.5 rounded-[6px] bg-lemon p-5 pt-6 transition-transform duration-500 ease-spring hover:-translate-y-1 hover:rotate-0"
      style={
        {
          rotate: `${TILTS[index % TILTS.length]}deg`,
          '--delay': `${index * 0.06}s`,
        } as CSSProperties
      }
    >
      {index % 2 === 0 ? (
        <Pin className="-top-2 left-1/2 -translate-x-1/2" />
      ) : (
        <Sticker name="paperclip" size={42} rotate={-20} className="absolute -top-5 right-4" />
      )}
      <Sticker name={look.sticker} size={32} rotate={8} className="absolute right-3 bottom-3" />
      <span className="truncate text-[13px] font-semibold text-ink/55">{hw.subject}</span>
      <span className="line-clamp-3 flex-1 font-display text-[17px] leading-[22px] font-semibold text-ink">
        {hw.title}
      </span>
      <span
        className={`pr-10 text-[13px] font-semibold ${hw.dueOn < today ? 'text-red-pen' : 'text-ink/50'}`}
      >
        {due}
      </span>
    </Link>
  );
}

type State = 'open' | 'late' | 'queued' | 'submitted' | 'returned' | 'accepted' | 'reading';

const MARK: Record<State, { sign: string; className: string }> = {
  open: { sign: '', className: 'bg-transparent' },
  late: { sign: '!', className: 'bg-[#e5484d] text-white border-[#e5484d]' },
  queued: { sign: '⏳', className: 'bg-amber-50 border-amber-200' },
  submitted: { sign: '✓', className: 'bg-[#eee9ff] text-live border-[#eee9ff]' },
  returned: { sign: '↺', className: 'bg-amber-100 text-amber-700 border-amber-100' },
  accepted: { sign: '✓', className: 'bg-[#1fa571] text-white border-[#1fa571]' },
  reading: { sign: '•', className: 'bg-fill text-muted border-fill' },
};

/** One homework as a sheet of «лінійка»; open it to read the task and write the answer. */
function TaskSheet({
  hw,
  submission,
  schoolId,
  studentId,
  tint,
}: {
  hw: HomeworkItem;
  submission: Submission | undefined;
  schoolId: string;
  studentId: string;
  tint: Swatch;
}) {
  const { t } = useTranslation();
  const actions = useHomeworkActions();
  const today = todayIso();
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState(submission?.body ?? '');
  const [attachments, setAttachments] = useState<string[]>(submission?.attachments ?? []);
  // made once per answer: a retry after a lost connection is recognised by the server
  const [clientId] = useState(() => crypto.randomUUID());
  const queued = useQueuedActions<HomeworkAnswer>(OFFLINE_ACTIONS.submitHomework).some(
    (a) => a.homeworkId === hw.id && a.studentId === studentId,
  );
  const state: State = queued
    ? 'queued'
    : !hw.requiresSubmission
      ? 'reading'
      : !submission
        ? hw.dueOn < today
          ? 'late'
          : 'open'
        : submission.status === 'accepted'
          ? 'accepted'
          : submission.status === 'returned'
            ? 'returned'
            : 'submitted';
  const due =
    hw.dueOn === today
      ? t('homework.dueToday')
      : hw.dueOn < today
        ? t('homework.overdue', { date: shortDate(hw.dueOn) })
        : t('student.homework.due', { date: shortDate(hw.dueOn) });
  const canAnswer = hw.requiresSubmission && submission?.status !== 'accepted';
  const statusKey =
    state === 'late' || state === 'open' ? null : state === 'reading' ? 'notRequired' : state;

  return (
    <article className="paper-lined motion-rise relative overflow-hidden rounded-[22px] pt-2.5 pr-5 pb-3 pl-14">
      <span className="absolute inset-y-0 left-10 w-[1.5px] bg-paper-margin" />
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex min-h-14 w-full items-start gap-3 text-left"
      >
        <span
          className={`absolute top-3.5 left-2.5 flex size-[26px] items-center justify-center rounded-full border-2 text-[13px] font-bold ${MARK[state].className}`}
          style={state === 'open' ? { borderColor: tint.deep } : undefined}
        >
          {MARK[state].sign}
        </span>
        <span className="flex-1">
          <span
            className={`block leading-[22px] font-semibold text-ink ${open ? '' : 'line-clamp-2'}`}
          >
            {hw.title}
          </span>
          <span
            className={`text-[13px] leading-5 font-semibold ${state === 'late' || hw.dueOn === today ? 'text-[#e5484d]' : 'text-muted'}`}
          >
            {due}
            {statusKey ? ` · ${t(`homework.status.${statusKey}`)}` : ''}
          </span>
        </span>
        <span
          className={`pt-1 text-lg text-faint transition-transform duration-500 ease-spring ${open ? 'rotate-180' : ''}`}
        >
          ⌄
        </span>
      </button>
      {open && (
        <div className="motion-rise space-y-3 pt-2">
          {hw.body && <p className="text-[15px] leading-7 text-ink-soft">{hw.body}</p>}
          <SpeakButton text={[hw.subject, hw.title, hw.body].filter(Boolean).join('. ')} />
          {hw.criteria && (
            <div className="rounded-2xl bg-fill p-3">
              <p className="text-sm font-semibold text-ink">✓ {t('homework.criteria')}</p>
              <ul className="mt-1 space-y-0.5 text-[15px] text-ink-soft">
                {hw.criteria
                  .split('\n')
                  .filter(Boolean)
                  .map((line) => (
                    <li key={line}>• {line}</li>
                  ))}
              </ul>
            </div>
          )}
          {submission?.feedback && (
            <div className="-rotate-[0.6deg] rounded-2xl bg-lemon p-3">
              <p className="text-sm font-semibold text-ink">{t('student.homework.teacherSays')}</p>
              <p className="text-[15px] leading-7 text-ink-soft">{submission.feedback}</p>
            </div>
          )}
          {canAnswer ? (
            <form
              className="space-y-3"
              onSubmit={(e: FormEvent) => {
                e.preventDefault();
                actions.submit.mutate({
                  schoolId,
                  homeworkId: hw.id,
                  courseId: hw.courseId,
                  studentId,
                  // a photo alone is an answer too
                  body: body.trim() || t('homework.files.onlyFiles'),
                  clientId,
                  attachments,
                  ...(submission && { existingId: submission.id }),
                });
              }}
            >
              <textarea
                aria-label={t('homework.answer')}
                className="min-h-28 w-full resize-y bg-transparent text-base leading-7 text-ink outline-none placeholder:text-faint"
                placeholder={t('student.homework.answerPlaceholder')}
                maxLength={10000}
                value={body}
                onChange={(e) => setBody(e.target.value)}
              />
              <AnswerFiles
                schoolId={schoolId}
                homeworkId={hw.id}
                studentId={studentId}
                value={attachments}
                onChange={setAttachments}
              />
              {queued && <p className="text-sm text-amber-800">⏳ {t('offline.queuedAnswer')}</p>}
              <button
                type="submit"
                disabled={
                  (!body.trim() && attachments.length === 0) || actions.submit.isPending || queued
                }
                className="rounded-full bg-ink px-6 py-2.5 font-semibold text-white transition-transform duration-500 ease-spring hover:-translate-y-0.5 disabled:opacity-35"
              >
                {queued
                  ? t('offline.waiting')
                  : actions.submit.isPending
                    ? t('homework.submitting')
                    : submission
                      ? t('homework.resubmit')
                      : t('homework.submit')}
              </button>
            </form>
          ) : (
            submission && (
              <div className="space-y-2">
                <p className="leading-7 text-muted">«{submission.body}»</p>
                <AttachmentList paths={submission.attachments} />
              </div>
            )
          )}
        </div>
      )}
    </article>
  );
}

/** The student's homework on a computer: burning notes, then subjects as folders in a notebook. */
export function StudentHomework({ ctx }: { ctx: StudentContext }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { course?: string };
  const today = todayIso();
  const studentId = ctx.child.memberId;
  const data = useStudentHomework(studentId, addDays(today, -14));
  const homework = data.data?.homework ?? [];
  const submissions = data.data?.submissions ?? [];
  const todo = homeworkToDo(homework, submissions, today, addDays(today, 1));
  const [showDone, setShowDone] = useState(false);

  const folders = [...new Set(homework.map((h) => h.courseId))]
    .map((courseId) => {
      const all = homework.filter((h) => h.courseId === courseId);
      return {
        courseId,
        subject: (all[0] as HomeworkItem).subject,
        all,
        late: all.filter((h) => todo.overdue.includes(h)),
        open: all.filter((h) => todo.open.includes(h)),
      };
    })
    .sort(
      (a, b) =>
        b.late.length - a.late.length ||
        b.open.length - a.open.length ||
        a.subject.localeCompare(b.subject, 'uk'),
    );
  const selected = folders.find((f) => f.courseId === search.course) ?? folders[0];
  const look = selected ? subjectLook(selected.subject) : undefined;
  const todoOf = selected ? [...selected.late, ...selected.open] : [];
  const done = selected ? selected.all.filter((h) => !todoOf.includes(h)).reverse() : [];
  const submissionOf = (id: string) => submissions.find((s) => s.homeworkId === id);
  const active = todo.open.length + todo.overdue.length;
  const burning = [...todo.overdue, ...todo.soon];

  return (
    <div className="space-y-2">
      <header className="flex flex-wrap items-end justify-between gap-4 rounded-[28px] bg-white px-7 py-6">
        <div>
          <p className="-rotate-2 font-hand text-2xl font-bold text-ink-soft">
            {t('student.homework.overline')}
          </p>
          <h1 className="font-display text-5xl font-bold tracking-tight text-ink">
            {t('student.homework.title')}
          </h1>
        </div>
        {active > 0 && (
          <span className="rounded-full bg-ink px-4 py-2 text-sm font-semibold text-white">
            {t('student.homework.active', { count: active })}
          </span>
        )}
      </header>

      {burning.length > 0 && (
        <section className="paper-grid rounded-[28px] px-7 pt-6 pb-8">
          <h2 className="mb-6 font-display text-2xl font-bold text-ink">
            {t('student.homework.burning')}
          </h2>
          <div className="grid grid-cols-2 gap-6 sm:grid-cols-3 lg:grid-cols-5">
            {burning.slice(0, 5).map((hw, i) => (
              <StickyNote key={hw.id} hw={hw} index={i} />
            ))}
          </div>
        </section>
      )}

      <NotebookSpread
        left={
          <section className="rounded-[28px] bg-[#bcd6fb] p-6 lg:pr-10">
            <h2 className="mb-6 font-display text-2xl font-bold text-ink">
              {t('student.homework.folders')}
            </h2>
            <div className="grid grid-cols-2 gap-x-5 gap-y-7 xl:grid-cols-3">
              {folders.map((f) => {
                const l = subjectLook(f.subject);
                const left = f.open.length + f.late.length;
                return (
                  <FolderTile
                    key={f.courseId}
                    title={f.subject}
                    subtitle={
                      left > 0
                        ? t('student.homework.tasks', { count: left })
                        : t('student.homework.noOpen')
                    }
                    colors={l.colors}
                    sticker={l.sticker}
                    alert={f.late.length > 0}
                    active={f.courseId === selected?.courseId}
                    onClick={() => {
                      setShowDone(false);
                      void navigate({
                        to: '/homework',
                        search: { course: f.courseId },
                        replace: true,
                      });
                    }}
                  />
                );
              })}
            </div>
          </section>
        }
        right={
          <section className="space-y-4 rounded-[28px] bg-white p-6 lg:pl-10">
            {selected && look && (
              <>
                <div className="relative flex min-h-28 items-end overflow-hidden rounded-[24px] bg-fill px-6 py-5 text-ink">
                  <div className="relative z-10">
                    <h2 className="font-display text-3xl font-bold">{selected.subject}</h2>
                    <p className="text-sm font-semibold text-muted">
                      {[
                        todoOf.length > 0
                          ? `${t('student.homework.open')}: ${todoOf.length}`
                          : t('student.homework.noOpen'),
                        done.length > 0 ? `${t('student.homework.done')}: ${done.length}` : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                  <Sticker
                    name={look.sticker}
                    size={104}
                    rotate={12}
                    className="absolute -right-1 -bottom-3"
                  />
                </div>
                {todoOf.map((hw) => (
                  <TaskSheet
                    key={hw.id}
                    hw={hw}
                    submission={submissionOf(hw.id)}
                    schoolId={ctx.schoolId}
                    studentId={studentId}
                    tint={look.colors}
                  />
                ))}
                {done.length > 0 && (
                  <button
                    type="button"
                    aria-expanded={showDone}
                    onClick={() => setShowDone(!showDone)}
                    className="rounded-full bg-fill px-4 py-2 text-sm font-semibold text-ink-soft"
                  >
                    {t('student.homework.done')} · {done.length} {showDone ? '⌃' : '⌄'}
                  </button>
                )}
                {showDone &&
                  done.map((hw) => (
                    <TaskSheet
                      key={hw.id}
                      hw={hw}
                      submission={submissionOf(hw.id)}
                      schoolId={ctx.schoolId}
                      studentId={studentId}
                      tint={look.colors}
                    />
                  ))}
              </>
            )}
          </section>
        }
      />
    </div>
  );
}
